import BaseModal from 'ext-ui/components/BaseModal/BaseModal'
import React, { useEffect } from 'react'
import { useTranslate } from 'src/localization/Trns'
import { useG2TokenMutation } from 'src/queries/g2/g2.queries'

import style from './G2ReviewModal.style'

interface G2ReviewModalProps {
  isOpen: boolean
  onClose: () => void
}

// The embed URL is built server-side, but validate the origin before
// rendering it in an iframe so a misconfigured/unexpected URL can't load.
const isG2EmbedUrl = (url: string): boolean => {
  try {
    return new URL(url).origin === 'https://www.g2.com'
  } catch {
    return false
  }
}

const G2ReviewModal: React.FC<G2ReviewModalProps> = ({ isOpen, onClose }) => {
  const translate = useTranslate()
  const { mutate, data, isPending, isError, reset } = useG2TokenMutation()

  useEffect(() => {
    if (isOpen) {
      mutate()
    }
  }, [isOpen])

  const handleClose = () => {
    reset()
    onClose()
  }

  const reviewEmbedUrl = data?.reviewEmbedUrl
  const hasValidEmbedUrl =
    reviewEmbedUrl !== undefined && isG2EmbedUrl(reviewEmbedUrl)
  const showError =
    isError || (reviewEmbedUrl !== undefined && !hasValidEmbedUrl)

  return (
    <BaseModal
      className="G2ReviewModal"
      css={style}
      isOpen={isOpen}
      onClose={handleClose}
      aria-label={translate('G2 Review Form')}
    >
      {isPending && (
        <div className="G2ReviewModal-loading" data-testid="g2-review-loading">
          <span>{translate('Loading...')}</span>
        </div>
      )}
      {showError && (
        <div className="G2ReviewModal-error" data-testid="g2-review-error">
          <span>
            {translate('Something went wrong. Please try again later.')}
          </span>
        </div>
      )}
      {hasValidEmbedUrl && (
        <iframe
          className="G2ReviewModal-iframe"
          data-testid="g2-review-iframe"
          src={reviewEmbedUrl}
          title={translate('G2 Review Form')}
        />
      )}
    </BaseModal>
  )
}

export default G2ReviewModal
